import { TextInput, TextInputProps } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form/lib/use-form"
import awesomePhonenumber from "awesome-phonenumber"
import React, { useContext } from "react"
import NumberFormat from "react-number-format"
import { EditingContext } from "../editingContext"
import { KeysOfType } from "~/utils/types"
import { ReadOnlyInput } from "./ReadOnlyInput"

const phoneNumber = (value: string) => {
  const ayt = awesomePhonenumber.getAsYouType("CA")
  ayt.reset(value)
  return ayt.getPhoneNumber()
}

export const phoneNumberFormat = (value: string | null) =>
  value ? phoneNumber(value).getNumber("national") : ""

export const validatePhoneNumber = (value: string | null) =>
  value && awesomePhonenumber(value, "CA").isValid() ? null : "Please enter a valid phone number"

type PhoneNumberInputProps<
  T extends Record<K, string | null>,
  K extends KeysOfType<T, string | null>
> = Omit<TextInputProps, "form" | "defaultValue" | "type" | "value" | "error"> & {
  form: UseFormReturnType<T>
  field: K
}

export const PhoneNumberInput = <
  T extends Record<K, string | null>,
  K extends KeysOfType<T, string | null>
>({
  form,
  field,
  onBlur,
  autoComplete,
  ...props
}: PhoneNumberInputProps<T, K>) => {
  const editing = useContext(EditingContext)

  return editing ? (
    <NumberFormat
      value={form.values[field] as string}
      onValueChange={({ value }) => {
        const pn = phoneNumber(value)
        form.setFieldValue(field, (pn.isPossible() ? pn.getNumber("significant") : value) as T[K])
      }}
      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
        form.validateField(field)
        onBlur && onBlur(event)
      }}
      error={form.errors[field]}
      customInput={TextInput}
      format={(value) => {
        const pn = phoneNumber(value)
        return pn.isPossible() ? pn.getNumber("national") : value
      }}
      isNumericString
      type="tel"
      autoComplete={autoComplete ?? "tel"}
      {...props}
    />
  ) : (
    <ReadOnlyInput
      value={phoneNumberFormat(form.values[field])}
      error={field in form.errors}
      {...props}
    />
  )
}
